import React from "react";
import { Gift, ArrowRight, EyeOff, Sparkles } from "lucide-react";
import { useLang } from "@/App";

const PROMO_IMG =
  "https://customer-assets.emergentagent.com/job_audicion-amplisoon/artifacts/6oorpqxo_2023-Signia-IX-05.png";

export default function PromoBanner() {
  const { t } = useLang();
  return (
    <section
      className="py-16 md:py-24 bg-gradient-to-br from-blue-950 via-blue-900 to-sky-700 text-white relative overflow-hidden"
      data-testid="promo-banner"
    >
      {/* Ambient glow background */}
      <div
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "radial-gradient(circle at 75% 50%, rgba(56,189,248,0.35) 0%, transparent 45%), radial-gradient(circle at 15% 20%, rgba(147,197,253,0.15) 0%, transparent 40%)",
        }}
      />
      {/* Diagonal accent */}
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] bg-sky-400/20 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 relative grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Left: copy */}
        <div className="lg:col-span-7 order-2 lg:order-1">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm text-sm font-semibold text-sky-100 mb-5">
            <Sparkles className="w-4 h-4 text-amber-300" />
            {t.promo.invisibleBadge}
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
            {t.promo.title}
          </h2>

          <p className="mt-5 text-lg md:text-xl text-sky-50 leading-relaxed max-w-xl">
            {t.promo.text}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a
              href="#contacto"
              className="group inline-flex items-center gap-3 px-8 h-14 bg-white text-blue-950 font-bold rounded-xl shadow-2xl hover:shadow-sky-500/40 transition-all hover:-translate-y-0.5"
              data-testid="promo-cta"
            >
              <Gift className="w-5 h-5" />
              {t.promo.cta}
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </a>
            <div className="inline-flex items-center gap-2 text-sky-100 text-sm font-medium">
              <EyeOff className="w-4 h-4" />
              Tan pequeños que nadie los nota
            </div>
          </div>
        </div>

        {/* Right: hero image */}
        <div className="lg:col-span-5 order-1 lg:order-2 relative">
          <div className="relative w-full max-w-[520px] mx-auto">
            {/* Soft glow behind image */}
            <div className="absolute -inset-8 bg-gradient-to-br from-sky-300/30 via-blue-400/15 to-transparent rounded-[40px] blur-3xl" />

            {/* Clean image, no tint, no frame */}
            <img
              src={PROMO_IMG}
              alt="Audífonos invisibles Signia Silk en su estuche"
              className="relative w-full h-auto object-contain drop-shadow-[0_25px_50px_rgba(0,0,0,0.5)]"
              data-testid="promo-image"
            />

            {/* Floating spec card */}
            <div className="absolute bottom-2 -left-2 md:-left-6 bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl px-5 py-3 border border-white/40">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">
                Signia
              </div>
              <div className="text-lg font-extrabold text-blue-950 leading-tight">
                Silk IX
              </div>
            </div>

            {/* Top right badge */}
            <div className="absolute top-2 -right-2 md:top-4 md:right-2 bg-amber-400 text-blue-950 text-xs font-extrabold uppercase tracking-wider px-3 py-1.5 rounded-full shadow-lg rotate-3">
              30 días gratis
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
